import React, { useState, useCallback } from 'react';
import { Keyboard, View } from 'react-native';
import { useDelayedAutoFocus } from '@/hooks/useDelayedAutoFocus';
import { IContextSheetContentProps, ISheetConfig } from '@/components/ui/ContextSheet';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';
import { Textarea, TextareaInput } from '@/components/gluestack/textarea';
import { Input, InputField } from '@/components/gluestack/input';
import { ButtonWrapper } from '@/components/ui/ButtonWrapper';
import { Tick02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { useTranslation } from 'react-i18next';


export interface ITextEditSheetProps extends IContextSheetContentProps {
  /** Current text value */
  value?: string | null;
  /** Callback when text is saved */
  onSave?: (text: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Maximum character limit */
  maxLength?: number;
  /** Title shown in the sheet */
  title?: string;
  /** Whether to allow multiline input (default: true) */
  multiline?: boolean;
  /** Whether to enable autocorrect (default: true) */
  autoCorrect?: boolean;
  /** Auto-capitalize mode (default: 'sentences') */
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  /** Disable password managers (default: false) */
  disablePasswordManager?: boolean;
}

export default function TextEditSheet({
  onClose,
  payload,
  value: directValue,
  onSave: directOnSave,
  placeholder: directPlaceholder,
  maxLength: directMaxLength,
  multiline: directMultiline,
  autoCorrect: directAutoCorrect,
  autoCapitalize: directAutoCapitalize,
  disablePasswordManager: directDisablePasswordManager,
}: ITextEditSheetProps & { payload?: Partial<ITextEditSheetProps> }) {
  const value = payload?.value ?? directValue ?? '';
  const onSave = payload?.onSave ?? directOnSave;
  const placeholder = payload?.placeholder ?? directPlaceholder;
  const maxLength = payload?.maxLength ?? directMaxLength ?? 2000;
  const multiline = payload?.multiline ?? directMultiline ?? true;
  const autoCorrect = payload?.autoCorrect ?? directAutoCorrect ?? true;
  const autoCapitalize = payload?.autoCapitalize ?? directAutoCapitalize ?? 'sentences';
  const disablePasswordManager = payload?.disablePasswordManager ?? directDisablePasswordManager ?? false;

  const { t } = useTranslation();


  const [localText, setLocalText] = useState(value ?? '');
  const inputRef = useDelayedAutoFocus();

  const handleSave = useCallback(() => {
    Keyboard.dismiss();
    onSave?.(localText.trim());
    onClose?.();
  }, [localText, onSave, onClose]);

  const characterCount = localText.length;
  const isOverLimit = characterCount > maxLength;

  return (
    <VStack className="flex-1 gap-3">
      {/* Text input area */}
      <VStack className={multiline ? 'flex-1' : ''}>
        {multiline ? (
          <Textarea
            size="md"
            className="flex-1"
            isInvalid={isOverLimit}>
            <TextareaInput
              ref={inputRef}
              value={localText}
              onChangeText={setLocalText}
              placeholder={placeholder ?? t('editProfile.textPlaceholder', 'Enter text...')}
              multiline
              textAlignVertical="top"
              autoCorrect={autoCorrect}
              autoCapitalize={autoCapitalize}
              className="m-0 p-2"
            />
          </Textarea>
        ) : (
          <Input
            size="md"
            variant="outline"
            isInvalid={isOverLimit}
            className="">
            <InputField
              ref={inputRef}
              value={localText}
              onChangeText={setLocalText}
              placeholder={placeholder ?? t('editProfile.textPlaceholder', 'Enter text...')}
              autoCorrect={autoCorrect}
              autoCapitalize={autoCapitalize}
              returnKeyType="done"
              submitBehavior="blurAndSubmit"
              onSubmitEditing={handleSave}
              autoComplete={disablePasswordManager ? 'off' : undefined}
              textContentType={disablePasswordManager ? 'none' : undefined}
              importantForAutofill={disablePasswordManager ? 'no' : undefined}
            />
          </Input>
        )}

      </VStack>

      {!multiline && <View className="flex-1" />}

      <HStack className="shrink-0 items-center justify-between px-1">
        <Text
          size="sm"
          className={isOverLimit ? 'text-error-500' : 'text-typography-500'}>
          {characterCount} / {maxLength}
        </Text>
        <ButtonWrapper icon={Tick02Icon} text={t('common.done', 'Done')} onPress={handleSave} isDisabled={isOverLimit} />
      </HStack>
    </VStack>
  );
}

TextEditSheet.sheetConfig = {
  largeScreenMode: 'modal',
  adjustForWebKeyboard: true,
  expectsKeyboard: true,
} satisfies ISheetConfig;
